import React, { type ComponentProps, forwardRef, type Ref } from 'react';
import type { Text as RNText } from 'react-native';

import { BodyText } from '../BodyText';
import type { DisplayText } from '../DisplayText';
import type { LabelText } from '../LabelText';
import type { TitleText } from '../TitleText';
import { highlightWords } from './HighlightWords.helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const HighlightWords = forwardRef(
  <ComponentType extends TextComponent = typeof BodyText>(
    props: HighlightWordsProps<ComponentType>,
    ref: Ref<RNText> | undefined,
  ) => {
    const { as = BodyText, text, highlights = [], ...restProps } = props;

    const Component = as as React.ComponentType<ComponentProps<ComponentType>>;

    const castedProps = restProps as ComponentProps<typeof Component>;
    const substrings = highlightWords(text, highlights);

    // ─────────────────────────────────────────────────────────────────────

    if (substrings.length === 1) {
      const [{ substring, shouldHighlight }] = substrings;

      return (
        <Component bold={shouldHighlight} {...castedProps}>
          {substring}
        </Component>
      );
    }

    // ─────────────────────────────────────────────────────────────────────
    return (
      <Component ref={ref} {...castedProps}>
        {substrings.map(({ key, substring, shouldHighlight }) => (
          <Component key={key} bold={shouldHighlight} {...castedProps}>
            {substring}
          </Component>
        ))}
      </Component>
    );
  },
);

// ─── Types ───────────────────────────────────────────────────────────────────

type TextComponent =
  | typeof BodyText
  | typeof DisplayText
  | typeof TitleText
  | typeof LabelText;

type HighlightWordsProps<
  ComponentType extends TextComponent = typeof BodyText,
> = {
  text: string;
  highlights?: readonly string[];

  /** Polymorphic component to render as */
  as?: ComponentType;
} & ComponentBaseProps<ComponentType>;

type ComponentBaseProps<ComponentType extends TextComponent> = Omit<
  ComponentProps<ComponentType>,
  'children' | 'bold' | 'ref'
>;
